<template>
    <view-wrapper
      :show-back-button="false"
      :name="title"
      :loading="loading"
      loading-text="Getting connectors"
    >
      <filter-and-sort-box
        :data="connectors"
        :headers="headers"
        :use-search="true"
        @searchQuery="onSearch"
        defaultSortKey="created"
        :defaultSortOrder="1"
      >
        <template v-slot:d="{ d }">
          <td
            class="relative py-1"
            v-for="(key, index) in getKeys(d)"
            :key="key + index"
            @click="editConnector(d)"
          >
            <div
              class="w-full py-4 pl-4 bg-primary-light"
              v-if="Array.isArray(d[key])"
            >
              <span v-for="(item, index) in d[key]" :key="index">
                {{ item.name }}<span v-if="index < d[key].length - 1">,</span>
              </span>
            </div>
            <div
              class="w-full py-4 pl-4 bg-primary-light"
              :class="{
                'rounded-l': index === 0,
                'rounded-r': Object.keys(headers).length - 1 === index,
              }"
              v-else
            >
              <span v-if="key != 'modified' && key != 'active'" class="inline-block w-10/12">{{
                d[key] ? d[key] : ""
              }}</span>
              <span v-if="key == 'active'" class="inline-block w-10/12">{{
                d[key] ? "Yes" : "No"
              }}</span>
              <span v-if="key == 'modified'" class="inline-block w-10/12">{{
                d[key] ? formatDate(d[key]) : ""
              }}</span>
              <i
                v-if="Object.keys(headers).length - 1 === index"
                class="p-1 mt-1 mr-1 arrow right"
              />
            </div>
          </td>
        </template>
      </filter-and-sort-box>
      <div class="py-2">
        <div class="float-right">
          <i
            class="p-1 mx-1 mt-1 cursor-pointer arrow left"
            @click="setPage(page - 1)"
          />
          <span class="select-none">Page {{ page }}</span>
          <i
            class="p-1 mx-1 mt-1 cursor-pointer arrow right"
            @click="setPage(page + 1)"
          />
        </div>
      </div>
      <modal
        v-if="errorMessage && errorMessage.length"
        type="warning"
        @close="errorMessage = null"
      >
        <template slot="body">
          <h2>Error</h2>
          <p>{{ errorMessage }}</p>
        </template>
      </modal>
    </view-wrapper>
  </template>
  
  <script>
  import { BehaviorSubject, combineLatest } from "rxjs";
  import { debounceTime, distinctUntilChanged, mapTo } from "rxjs/operators";
  
  import FilterAndSortBox from "../components/FilterAndSortBox";
  import { formatDate } from "../utils/dateHelpers";
  import Modal from "../components/Modal.vue";
  
  export default {
    components: {
      FilterAndSortBox,
      Modal,
    },
    data: () => ({
      loading: false,
      connectors: [],
      headers: {
        ipAddress: "IP Address",
        port: "Port",
        protocol: "Protocol",
        status: "Status",
        active: "Active"
      },
      page: 1,
      pageSize: 10,
      searchQuery: null,
      pageNumber$: null,
      pageSize$: null,
      searchQuery$: null,
      externalIdentifier: "",
      isPrinting: false,
      printQueues: null,
      errorMessage: null,
    }),
    computed: {
      title() {
        if (this.$route.params.connectorId) return "Select connector";
  
        return "Connectors";
      },
    },
    methods: {
      async getConnectors(searchQuery, page, pageSize) {
        this.loading = true;
        let response;
        if (this.$route.params.connectorId) {
          response = await this.$store.$api.get(
            `/api/connectors/${
              this.$route.params.connectorId
            }/?take=${pageSize}&offset=${pageSize *
              (page - 1)}&searchQuery=${searchQuery}`
          );
        } else {
          response = await this.$store.$api.get(
            `/api/connectors?take=${pageSize}&offset=${pageSize *
              (page - 1)}&searchQuery=${searchQuery}`
          );
        }
        this.connectors = response.data.data;
        this.loading = false;
      },
      getKeys(order) {
        return Object.keys(order).filter(key => {
          return key !== "id";
        });
      },
      formatDate,
      editConnector(connector) {
        this.$router.push("/connectors/" + connector.id);
      },
      setPage(newPage) {
        this.pageNumber$.next(Math.max(1, newPage));
      },
      onSearch(value) {
        this.searchQuery$.next(value);
      },
    },
    async mounted() {
      this.pageNumber$ = new BehaviorSubject(1);
      this.pageNumber$$ = this.pageNumber$.subscribe(
        value => (this.page = value)
      );
  
      this.searchQuery$ = new BehaviorSubject("");
      this.searchQuery$$ = this.searchQuery$
        .pipe(
          distinctUntilChanged(),
          mapTo(1)
        )
        .subscribe(value => {
          this.pageNumber$.next(value);
        });
      this.pageSize$ = new BehaviorSubject(10);
  
      this.searchStream$$ = combineLatest(
        this.searchQuery$.pipe(distinctUntilChanged()),
        this.pageNumber$.pipe(
          debounceTime(300),
          distinctUntilChanged()
        ),
        this.pageSize$
      )
        .pipe(debounceTime(300))
        .subscribe(([searchQuery, pageNumber, pageSize]) => {
          this.getConnectors(
            encodeURIComponent(searchQuery || ""),
            pageNumber,
            pageSize
          );
        });
    },
    beforeDestroy() {
      this.pageNumber$$.unsubscribe();
      this.searchQuery$$.unsubscribe();
      this.searchStream$$.unsubscribe();
    },
  };
  </script>
  
  <style scoped>
  .small-spinner-animation {
    border-color: rgba(255, 255, 255, 0.25);
    border-top-color: rgba(255, 255, 255, 1);
    border-radius: 50%;
    animation: smallSpinnerRotation 0.6s infinite linear;
  }
  
  @keyframes smallSpinnerRotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
  </style>
  